import { Alert, Button, Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { first } from 'lodash';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { shouldRemountFormForServerData } from '@/components/form/utils/shouldRemountFormForServerData';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { DispositiveProvisionsFormV2 } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { EditEntitySplitScreenBodySkeleton } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenBodySkeleton';
import { EditEntitySplitScreenFooterActionsPlaceholder } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenFooter';
import { EditTestamentaryEntitySection } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreen.types';
import { useOnHouseholdWorkflowTrackerComplete } from '@/modules/workflowTrackers/hooks/useOnHouseholdWorkflowTrackerComplete';
import { WorkflowTrackerKind } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { useOptionalDispositiveDiagramSyncContext } from '../contexts/DispositiveDiagramSync.context';
import {
  DispositiveProvisionsCalculateValues,
  DispositiveProvisionsContext,
  DispositiveProvisionsFormVariant,
  RootTargetContext,
} from '../contexts/dispositiveProvisions.context';
import {
  GetDispositiveProvisionsQuery,
  useGetDispositiveProvisionsLazyQuery,
} from '../DispositiveProvisionsForm/graphql/GetDispositiveProvisions.generated';
import {
  clientOrganizationContext,
  clientProfileContext,
  DispositiveProvisionsContextDataOrLoading,
  DispositiveProvisionsContextLoaded,
  entityContext,
  testamentaryEntityContext,
  useDispositiveProvisionsContextData,
} from '../hooks/useDispositiveProvisionsContextData';

interface DispositiveProvisionsModalBodyProps {
  onClose: () => void;
  onSwitchSectionAfterSubmit?: (afterSubmitSection?: EditEntitySection) => void;
  firstDeathClientId: string;
  headerRef: RefObject<HTMLDivElement | null>;
  footerRef: RefObject<HTMLDivElement | null>;
  nodeValue?: Decimal;
  variant: DispositiveProvisionsFormVariant;
  isFromTemplateEditor?: boolean;
  /** Called after any successful persist; see {@link DispositiveProvisionsFormProps.onDidSave}. */
  onDidSave?: () => void;
  /** Handler for section switching when variant is 'editTestamentaryEntity' */
  onTestamentaryEntitySectionSubmit?: (
    afterSubmitSection?: EditTestamentaryEntitySection
  ) => void;
}

/**
 * DispositiveProvisionsModalBody is the "base" component for rendering the
 * dispositive provisions form as the right-hand side content of a SplitScreenModal.
 *
 * @param onClose
 * @param onSwitchSectionAfterSubmit
 * @param firstDeathClientId
 * @param headerRef
 * @param footerRef
 * @param nodeValue
 * @param variant
 * @param isFromTemplateEditor
 * @constructor
 */
export function DispositiveProvisionsModalBody({
  onClose,
  onSwitchSectionAfterSubmit,
  firstDeathClientId,
  headerRef,
  footerRef,
  nodeValue,
  variant,
  isFromTemplateEditor,
  onDidSave,
  onTestamentaryEntitySectionSubmit,
}: DispositiveProvisionsModalBodyProps) {
  // Full provider payload (includes formVariant) once built
  const [contextData, setContextData] = useState<
    DispositiveProvisionsContext | undefined
  >();
  // React Hook Form reads defaultValues only when it mounts. Increment this
  // version when the current target receives changed server data.
  const [contextVersion, setContextVersion] = useState(0);

  // Cache of the very first loaded context – used as the "root" invariant
  // snapshot when we begin editing non-root targets.
  const [rootContext, setRootContext] = useState<
    DispositiveProvisionsContextLoaded | undefined
  >();

  // Apollo lazy query for loading a new target after a diagram click.
  const [loadDPData, { data: switchData, loading: switchLoading }] =
    useGetDispositiveProvisionsLazyQuery({ fetchPolicy: 'no-cache' });

  // A persisted form must not submit its old scenario IDs again. Keep it
  // unmounted until the requested target's canonical data has been applied.
  const savedTargetId = useRef<string | null>(null);
  const [savedReload, setSavedReload] = useState<{
    data?: GetDispositiveProvisionsQuery;
    failed?: boolean;
  }>();

  const reloadSavedTarget = useCallback(
    async (id: string) => {
      savedTargetId.current = id;
      setSavedReload({});
      try {
        const result = await loadDPData({
          variables: {
            id,
            waterfallWhere: { id: rootContext?.waterfall?.id ?? null },
            isOnWaterfall: Boolean(rootContext?.waterfall?.id),
            hypotheticalWaterfallId: rootContext?.isOnHypotheticalWaterfall
              ? (rootContext.waterfall?.id ?? null)
              : null,
          },
        });
        if (savedTargetId.current !== id) return;
        if (result.error || !result.data?.node) {
          setSavedReload({ failed: true });
          return;
        }
        setSavedReload({ data: result.data });
      } catch {
        if (savedTargetId.current === id) setSavedReload({ failed: true });
      }
    },
    [loadDPData, rootContext]
  );

  // The active-query refetch updates Apollo, but this modal renders from its
  // context snapshot. Reload the open target so the effect below can replace
  // that snapshot with the latest server data.
  const refreshCurrentTarget = useCallback(() => {
    // The saved-target reload owns the next snapshot. A background refresh
    // here could reload the old target during a diagram switch.
    if (!contextData || savedTargetId.current) return;

    void loadDPData({
      variables: {
        id: contextData.id,
        waterfallWhere: { id: rootContext?.waterfall?.id ?? null },
        isOnWaterfall: Boolean(rootContext?.waterfall?.id),
        hypotheticalWaterfallId: rootContext?.isOnHypotheticalWaterfall
          ? (rootContext.waterfall?.id ?? null)
          : null,
      },
    });
  }, [contextData, loadDPData, rootContext]);

  useOnHouseholdWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.HouseholdChatInteraction,
    onCompleted: refreshCurrentTarget,
  });

  // Context for suggestions, used to fetch new suggestions when switching targets.
  const { setTargetId } = useDispositiveProvisionSuggestionsContext();

  // When a non-root tile has been committed (edit-entity diagram navigation),
  // the body loads that target directly instead of bootstrapping the root
  // context first. Optional: standalone modals render without this provider.
  const committedTargetNode =
    useOptionalDispositiveDiagramSyncContext()?.committedTargetNode ?? null;

  /* ------------------------------------------------------------------ */
  /*        EFFECT: Build initial context for root target               */
  /* ------------------------------------------------------------------ */

  const data = useDispositiveProvisionsContextData({
    currentNodeValue: nodeValue,
    firstDeathPrimaryClientId: firstDeathClientId,
  });

  // Build root context once.
  useEffect(() => {
    if (data.loading || rootContext) return;

    const { loading, ...loaded } = data;
    setRootContext(loaded);

    // If a non-root tile is already committed (edit-entity diagram
    // navigation), load that target directly and leave `contextData` unset so
    // the skeleton stays up until the target's context resolves. Building the
    // root context here would flash the root form between the skeleton and the
    // selected tile's form.
    if (committedTargetNode && committedTargetNode.pulid !== loaded.id) {
      void loadDPData({
        variables: {
          id: committedTargetNode.pulid,
          waterfallWhere: { id: loaded.waterfall?.id ?? null },
          isOnWaterfall: Boolean(loaded.waterfall?.id),
        },
      });
      const targetKind = getPulidKind(committedTargetNode.pulid);
      if (
        targetKind === PulidKind.Entity ||
        targetKind === PulidKind.TestamentaryEntity
      ) {
        setTargetId(committedTargetNode.pulid);
      }
      return;
    }

    const rootPayload = buildProviderPayload(loaded, {
      variant,
      isFromTemplateEditor: !!isFromTemplateEditor,
      isEditingRootTarget: true,
    });

    diagnostics.debug(
      '[DispositiveProvisionsModalBody] Building root context and initial context data',
      {
        rootPayload,
      }
    );

    setContextData(rootPayload);
  }, [
    data,
    rootContext,
    variant,
    isFromTemplateEditor,
    committedTargetNode,
    loadDPData,
    setTargetId,
  ]);

  /* ------------------------------------------------------------------ */
  /*        EFFECT: Build new context after lazy query completes         */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    const result = savedReload ? savedReload.data : switchData;
    if (!result || switchLoading || !rootContext) return;

    const node = result.node;
    if (!node) {
      diagnostics.debug(
        '[DispositiveProvisionsModalBody] switchData.node was undefined',
        {
          switchData,
        }
      );
      return;
    }

    const waterfall = first(getNodes(result.dispositiveProvisionWaterfalls));

    // Re-use household details from the root context (they don't change)
    const householdDetails = {
      primaryClients: rootContext.primaryClients,
      householdId: rootContext.householdId,
    } as const;

    // Re-use showCalculatedDispositions state from previous context so the
    // toggle doesn't reset on every switch.
    const calculateTuple: DispositiveProvisionsCalculateValues = [
      rootContext.showCalculatedDispositions,
      rootContext.setShowCalculatedDispositions,
    ];

    let updated: DispositiveProvisionsContextDataOrLoading | undefined;

    if (node.__typename === 'Entity') {
      diagnostics.debug(
        '[DispositiveProvisionsModalBody] Building context for Entity node',
        {
          node,
        }
      );
      updated = entityContext({
        householdDetails,
        node,
        value: node.subtype.currentValue,
        calculateTuple,
        waterfall,
      });
    } else if (node.__typename === 'TestamentaryEntity') {
      diagnostics.debug(
        '[DispositiveProvisionsModalBody] Building context for TestamentaryEntity node',
        {
          node,
        }
      );
      updated = testamentaryEntityContext({
        householdDetails,
        node,
        value: new Decimal(0), // Outside of a Waterfall, TE's don't have a value.
        calculateTuple,
        waterfall,
      });
    } else if (node.__typename === 'ClientProfile') {
      diagnostics.debug(
        '[DispositiveProvisionsModalBody] Building context for ClientProfile node',
        {
          node,
        }
      );
      updated = clientProfileContext({
        householdDetails,
        node,
        value: new Decimal(0), // Outside of a Waterfall, CP's don't have a value.
        calculateTuple,
        waterfall,
      });
    } else if (node.__typename === 'ClientOrganization') {
      diagnostics.debug(
        '[DispositiveProvisionsModalBody] Building context for ClientOrganization node',
        {
          node,
        }
      );
      updated = clientOrganizationContext({
        householdDetails,
        node,
        value: new Decimal(0), // Outside of a Waterfall, CO's don't have a value.
        calculateTuple,
        waterfall,
      });
    }

    if (updated && !updated.loading) {
      const isRoot = updated.id === rootContext.id;

      const providerPayload = buildProviderPayload(updated, {
        variant,
        isFromTemplateEditor: !!isFromTemplateEditor,
        isEditingRootTarget: isRoot,
        rootTargetSnapshot: isRoot ? undefined : rootContext,
      });

      const shouldRemount = shouldRemountFormForServerData(
        contextData,
        providerPayload
      );
      // Keep the current form instance when the refetch returns no changes.
      // This avoids discarding in-progress user edits after a declined action.
      if (
        !savedReload &&
        contextData?.id === providerPayload.id &&
        !shouldRemount
      )
        return;

      if (isRoot) {
        setRootContext(updated);
      }
      if (shouldRemount) {
        setContextVersion((version) => version + 1);
      }
      setContextData(providerPayload);
      if (savedReload) {
        savedTargetId.current = null;
        setSavedReload(undefined);
      }
    }
  }, [
    switchData,
    switchLoading,
    rootContext,
    variant,
    isFromTemplateEditor,
    contextData,
    savedReload,
  ]);

  const handleSwitchTargetContextAfterSubmit = useCallback(
    async (selectedTargetId: string) => {
      diagnostics.debug(`[handleSwitchTargetContextAfterSubmit]`, {
        selectedTargetId,
      });

      // Fire the lazy query to fetch the DP data for the new target.
      if (savedTargetId.current) {
        await reloadSavedTarget(selectedTargetId);
      } else {
        await loadDPData({
          variables: {
            id: selectedTargetId,
            waterfallWhere: {
              id: rootContext?.waterfall?.id ?? null,
            },
            isOnWaterfall: Boolean(rootContext?.waterfall?.id),
          },
        });
      }

      // Also set the new targetID for the DP suggestions context.
      // This will trigger a refetch of the suggestions for the new target.
      const selectedTargetKind = getPulidKind(selectedTargetId);
      if (
        selectedTargetKind === PulidKind.Entity ||
        selectedTargetKind === PulidKind.TestamentaryEntity
      ) {
        setTargetId(selectedTargetId);
      }
    },
    [loadDPData, reloadSavedTarget, rootContext?.waterfall?.id, setTargetId]
  );

  if (savedReload?.failed) {
    return (
      <Stack spacing={2} padding={2}>
        <Alert severity="warning">
          Your changes were saved, but the form could not be reloaded.
        </Alert>
        <Button
          onClick={() => {
            if (savedTargetId.current)
              void reloadSavedTarget(savedTargetId.current);
          }}
        >
          Reload form
        </Button>
        <Button onClick={onClose}>Close</Button>
      </Stack>
    );
  }

  if (!contextData || savedReload) {
    return (
      <>
        <EditEntitySplitScreenBodySkeleton />
        {/* The footer's primary actions are portaled from the form, so they're
            absent while it loads. In the edit-entity trowser, keep disabled
            stand-ins present so they don't blink out. */}
        {variant === 'editEntity' && (
          <EditEntitySplitScreenFooterActionsPlaceholder
            footerRef={footerRef}
          />
        )}
      </>
    );
  }

  return (
    <DispositiveProvisionsContext.Provider value={contextData}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
        }}
      >
        {/* In the edit-entity trowser, loading is shown via the body skeleton
            and a target switch remounts the body, so suppress the overlay here
            to avoid a spinner flashing over the form right after the skeleton.
            Other variants keep it for their in-place target switch. */}
        {variant !== 'editEntity' && (
          <LoadingOverlay open={data.loading || switchLoading} />
        )}
        <DispositiveProvisionsFormV2
          key={`${contextData.id}:${contextVersion}`}
          headerRef={headerRef}
          footerRef={footerRef}
          onAfterSubmit={
            onSwitchSectionAfterSubmit ? onSwitchSectionAfterSubmit : onClose
          }
          onDidSave={({ isSwitchingTarget }) => {
            savedTargetId.current = contextData.id;
            setSavedReload({});
            if (!isSwitchingTarget) void reloadSavedTarget(contextData.id);
            onDidSave?.();
          }}
          onSwitchTargetContextAfterSubmit={
            handleSwitchTargetContextAfterSubmit
          }
          onClose={onClose}
          firstDeathClientId={firstDeathClientId}
          onTestamentaryEntitySectionSubmit={onTestamentaryEntitySectionSubmit}
        />
      </div>
    </DispositiveProvisionsContext.Provider>
  );
}

function buildProviderPayload(
  base: DispositiveProvisionsContextLoaded,
  options: {
    variant: DispositiveProvisionsFormVariant;
    isFromTemplateEditor: boolean;
    isEditingRootTarget: boolean;
    rootTargetSnapshot?: RootTargetContext;
  }
): DispositiveProvisionsContext {
  const {
    variant,
    isFromTemplateEditor,
    isEditingRootTarget,
    rootTargetSnapshot,
  } = options;
  return {
    ...base,
    formVariant: variant,
    isFromTemplateEditor,
    isEditingRootTarget,
    rootTargetContext: rootTargetSnapshot,
  };
}
